import { useState, useEffect, useCallback, useMemo } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';
import { useTranslation } from '../i18n';
import {
  rowsFromServices, rowsDirty, rowProblems, toTermsInput, serviceMeta, addRow,
} from '../screens/People/practitionerServices.js';
import { CREATE_CUSTOM_SERVICE } from './useMedicalService.js';

// What one practitioner provides at this clinic and how each service is
// delivered here (docs/CLINIC_PRACTITIONER_ENGAGEMENT_DESIGN.md §۴). The BFF
// joins the declaration, the performer set, the rule that actually applies and
// the service's cost facts, so the drawer asks once per person.
const FIELDS = `
  doctorId providesVisit currency
  services {
    declarationId currency catalogId description orderType category unitPrice priceIsPersonal clinicUnitPrice isActive
    schedulingMode durationMinutes prepInstructions
    isDefault isDefaultPerformer
    otherPerformers { type id label isDefault }
    commission { mode value basis } commissionIsPersonal
    generalCommission { mode value basis } generalCommissionKnown
    insuranceCoverageMode consumableCount sideCostTotalRial
  }
  available { currency declarationId description category orderType unitPrice isActive schedulingMode durationMinutes }
`;

const PRACTITIONER_SERVICES = `
  query PractitionerServices($doctorId: ID!) {
    practitionerServices(doctorId: $doctorId) { ${FIELDS} }
  }
`;

// Replace-all for THIS practitioner only — the BFF carries every other
// performer and every other rule on each service across.
const SET_TERMS = `
  mutation SetPractitionerServiceTerms($doctorId: ID!, $services: [PractitionerServiceTermsInput!]!) {
    setPractitionerServiceTerms(doctorId: $doctorId, services: $services) { ${FIELDS} }
  }
`;

/**
 * The data AND the edit buffer, for one practitioner.
 *
 * `doctorId` null = not loaded (a pending member, a staff row, or a reader who
 * may not see services). The buffer re-seeds when the person changes or a save
 * lands, and never on its own otherwise — a reload under a half-typed
 * percentage would erase it.
 */
export function usePractitionerServiceTerms(doctorId) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!doctorId);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (!doctorId) { setData(null); setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const d = await bffQuery(PRACTITIONER_SERVICES, { doctorId }, 'PractitionerServices');
      const next = d?.practitionerServices || null;
      setData(next);
      setRows(rowsFromServices(next));
      setError(null);
    } catch (e) {
      setData(null);
      setRows([]);
      setError(e?.message || t('people.services.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [doctorId, t]);

  useEffect(() => { load(); }, [load]);

  const dirty = !!data && rowsDirty(rows, data);
  const problems = useMemo(
    () => (dirty ? rowProblems(rows, serviceMeta(data)) : []),
    [dirty, rows, data],
  );

  const reset = useCallback(() => setRows(rowsFromServices(data)), [data]);

  const save = useCallback(async () => {
    try {
      const d = await bffMutation(SET_TERMS, { doctorId, services: toTermsInput(rows, data?.currency) }, 'SetPractitionerServiceTerms');
      const next = d?.setPractitionerServiceTerms || null;
      setData(next);
      setRows(rowsFromServices(next));
      return next;
    } catch (e) {
      // A PARTIAL failure saved some services: re-read so the buffer shows what
      // is really stored, then let the caller say which ones did not land. Any
      // other refusal wrote nothing, and the reader's edits stay on screen.
      if (e?.extensions?.code === 'PARTIAL_PRACTITIONER_SERVICES') await load();
      throw e;
    }
  }, [doctorId, rows, data, load]);

  /**
   * «تعریف خدمت همین‌جا» (PM-0350): invent a clinic service from the
   * practitioner's own tab, with THEM as its default performer, and have it
   * appear in their list without a trip to «کالاها و خدمات».
   *
   * One write for the service; a second, for the terms, only when this is the
   * practitioner's FIRST service and nothing unsaved was on screen — so their
   * first service becomes their default in the same act, while a later one
   * never displaces a visit, and an in-progress edit of another row is never
   * overwritten under the reader.
   * The re-read keeps every unsaved row edit; only the new service is added.
   */
  const createService = useCallback(async (input) => {
    const d = await bffMutation(CREATE_CUSTOM_SERVICE, {
      input: {
        ...input,
        performers: [{ type: 'DOCTOR', id: doctorId, isDefault: true }],
        defaultPerformerId: doctorId,
      },
    }, 'CreateCustomService');
    const created = d?.createCustomService || null;
    const fresh = await bffQuery(PRACTITIONER_SERVICES, { doctorId }, 'PractitionerServices');
    const next = fresh?.practitionerServices || null;
    const seeded = rowsFromServices(next);
    const kept = new Map(rows.map((r) => [r.declarationId, r]));
    let merged = seeded.map((r) => kept.get(r.declarationId) || r);
    for (const r of rows) if (!merged.some((m) => m.declarationId === r.declarationId)) merged.push(r);
    if (created?.id) merged = addRow(merged, created.id);
    const first = rows.length === 0;
    const wasDirty = !!data && rowsDirty(rows, data);
    if (created?.id && first && !wasDirty) {
      merged = merged.map((r) => ({ ...r, isDefault: r.declarationId === created.id }));
      const saved = await bffMutation(SET_TERMS, { doctorId, services: toTermsInput(merged, next?.currency) }, 'SetPractitionerServiceTerms');
      const settled = saved?.setPractitionerServiceTerms || next;
      setData(settled);
      setRows(rowsFromServices(settled));
      return created;
    }
    setData(next);
    setRows(merged);
    return created;
  }, [doctorId, rows, data]);

  return {
    data, loading, error, reload: load,
    rows, setRows, dirty, problems, invalid: problems.length > 0, reset, save, createService,
  };
}

export default usePractitionerServiceTerms;

// ── «خدمت نوبت» — what the booking modal picks from ──────────────────────────

const BOOKABLE_SERVICES = `
  query BookableServices($doctorId: ID!) {
    practitionerServices(doctorId: $doctorId) {
      doctorId
      providesVisit
      services {
        currency
        declarationId description orderType unitPrice priceIsPersonal clinicUnitPrice
        isActive isDefault durationMinutes
      }
    }
  }
`;

/**
 * The services this practitioner may be booked for, and which one to pre-select
 * (docs/CLINIC_RECEPTION_SERVICE_INVOICE_DESIGN.md §D2).
 *
 * The desk chooses a service ONCE, when it books, and never again — so this is
 * the only picker in the flow. The pre-selection is the practitioner's own
 * «خدمت پیش‌فرض», else their visit: the desk should be able to book without
 * touching the field at all on the common day.
 *
 * `providesVisit === false` is the state the modal warns about: nothing prices
 * this person's visit, so the proforma will open without a line and the fee is
 * typed at the desk. It is NOT a reason to refuse the booking.
 */
export function useBookableServices(doctorId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!doctorId);

  useEffect(() => {
    let cancelled = false;
    if (!doctorId) { setData(null); setLoading(false); return undefined; }
    setLoading(true);
    bffQuery(BOOKABLE_SERVICES, { doctorId }, 'BookableServices')
      .then((d) => { if (!cancelled) setData(d?.practitionerServices || null); })
      // An unreachable price list costs the pre-selection, never the booking:
      // the modal falls back to no service and the BFF resolves one server-side.
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [doctorId]);

  const services = useMemo(
    () => (data?.services || []).filter((s) => s.isActive !== false),
    [data],
  );

  // Their own default, else their visit, else nothing — and never a service the
  // clinic has not priced, which would quote the patient a figure of nothing.
  const defaultId = useMemo(() => {
    const priced = services.filter((s) => (s.unitPrice || 0) > 0);
    const own = priced.find((s) => s.isDefault);
    if (own) return own.declarationId;
    const visit = priced.find((s) => s.orderType === 'consultation');
    return visit ? visit.declarationId : null;
  }, [services]);

  return {
    services,
    defaultId,
    providesVisit: data ? !!data.providesVisit : true,
    loading,
  };
}
